import { SelectiveBloomEffect } from 'postprocessing';
import { computed, inject, nextTick, provide, ref, shallowRef, watch, watchEffect } from 'vue';

import { usePane, useResize } from '@resn/gozer-vue';
import { createLogger } from '~/utils/logger';

const logger = createLogger('bloom');

import { gpuTierSnapshot } from '~/composables/useGpuTier';

export const useBloom = () => {
    const bloom = inject('bloom', {});
    if (!bloom.init) {
        logger.warn('Bloom provider missing', {
            event: 'provider.missing',
            provider: 'BloomProvider',
            consumer: 'useBloom',
        });
        return { pass: null, enabled: true, setEnabled: () => {} };
    }
    return bloom;
};

export const BLOOM_INTENSITY = 2;

const applyBloomFromGpuTier = (
    result,
    { intensityScale, setEnabled, levels, scale, mipmapBlur, radius }
) => {
    let tier = typeof result?.tier === 'number' ? result.tier : 2;
    const type = result?.type;

    if (type === 'BENCHMARK_FETCH_FAILED' || type === 'FALLBACK') tier = Math.min(tier, 2);

    if (tier <= 0) {
        setEnabled(false);
        intensityScale.value = 0;
        return;
    }

    setEnabled(true);
    if (tier === 1) {
        intensityScale.value = 0.4;
        levels.value = 2;
        scale.value = 0.35;
        mipmapBlur.value = false;
        radius.value = Math.min(radius.value, 0.55);
    } else if (tier === 2) {
        intensityScale.value = 0.72;
        levels.value = 3;
        scale.value = 0.45;
        mipmapBlur.value = true;
    } else {
        intensityScale.value = 1;
        levels.value = 4;
        scale.value = 0.5;
        mipmapBlur.value = true;
    }
};

export const useBloomContext = () => {
    const pass = shallowRef(null);

    const enabled = ref(true);
    const enabledComputed = computed(() => enabled.value && intensity.value > 0);

    /** Multiplies timeline-driven bloom intensity (e.g. {@link BLOOM_INTENSITY}) after GPU tier runs. */
    const intensityScale = ref(1);

    // ― params
    const luminanceThreshold = ref(0.0);
    const luminanceSmoothing = ref(0.04);
    const intensity = ref(0);
    const radius = ref(0.76);
    const levels = ref(4);
    const mipmapBlur = ref(true);
    const scale = ref(0.5);

    const bloomTierCtx = () => ({
        intensityScale,
        setEnabled: (v) => {
            enabled.value = v;
        },
        levels,
        scale,
        mipmapBlur,
        radius,
    });

    watch(
        gpuTierSnapshot,
        (r) => {
            if (!r) return;
            applyBloomFromGpuTier(r, bloomTierCtx());
        },
        { immediate: true }
    );

    const init = (scene, camera) => {
        pass.value = new SelectiveBloomEffect(scene, camera, {
            luminanceThreshold: luminanceThreshold.value,
            luminanceSmoothing: luminanceSmoothing.value,
            intensity: intensity.value,
            radius: radius.value,
            levels: levels.value,
            mipmapBlur: mipmapBlur.value,
            resolutionScale: 0.5,
        });
        pass.value.ignoreBackground = true;

        setProps();

        return pass.value;
    };

    const setProps = () => {
        if (pass.value) {
            pass.value.intensity = intensity.value * intensityScale.value;
            pass.value.luminanceMaterial.threshold = luminanceThreshold.value;
            pass.value.luminanceMaterial.smoothing = luminanceSmoothing.value;
            pass.value.mipmapBlurPass.enabled = mipmapBlur.value;
            pass.value.mipmapBlurPass.radius = radius.value;
            pass.value.mipmapBlurPass.levels = levels.value;
        }
    };

    watchEffect(setProps);

    const resize = () => {
        nextTick(() => {
            const { width, height } = viewport;
            if (pass.value) pass.value.setSize(width * scale.value, height * scale.value);
        });
    };

    const viewport = useResize(resize, true);

    watch([scale, pass], resize);

    const data = {
        init,
        pass,
        luminanceThreshold,
        intensity,
        intensityScale,
        radius,
        enabled: enabledComputed,
        setEnabled: (bool) => (enabled.value = bool),
        reset: () => {
            intensity.value = 0;
        },
    };

    provide('bloom', data);

    usePane(
        [
            {
                name: 'luminanceThreshold',
                value: luminanceThreshold,
                options: { min: 0, max: 1, step: 0.01 },
            },
            { name: 'intensity', value: intensity, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'radius', value: radius, options: { min: 0, max: 5, step: 0.01 } },
            { name: 'levels', value: levels, options: { min: 1, max: 20, step: 1 } },
            { name: 'scale', value: scale, options: { min: 0, max: 1, step: 0.01 } },
        ],
        { title: 'Bloom', subfolder: false, expanded: false }
    );

    return data;
};
